import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import * as dayjs from 'dayjs';
import { PersonalLookupInfoForTestRegistrationDto, SaveChildUserAccountIdentityInfoRequest, UserAccountIdentityInfoDto } from 'src/app/core/dtos/dto';
import { BrandEnum, IdDocTypeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';

@Component({
  selector: 'app-proof-of-identity',
  templateUrl: './proof-of-identity.component.html',
  styleUrls: ['./proof-of-identity.component.scss']
})
export class ProofOfIdentityComponent implements OnInit {
  brandId: BrandEnum;
  brandName: string;

  @Input() userAccountId: number;
  @Input() personalLookupInfo: PersonalLookupInfoForTestRegistrationDto;
  @Input() forChildReadOnlyFields: boolean;

  @Output() formSubmit = new EventEmitter<SaveChildUserAccountIdentityInfoRequest>();

  namePattern = '[\\u0000-\\u007f]+';
  alphaNumericaPattern = '^[a-zA-Z0-9]+$';

  minimumAge = 14;
  proofOfIdentity: UserAccountIdentityInfoDto;
  proofOfIdentitySaveRequest: SaveChildUserAccountIdentityInfoRequest;
  testTypeName = "test";
  today = new Date();
  showBirthDateError: boolean;
  showIdDocExpiryError: boolean;
  userForm: FormGroup;

  get f() { return this.userForm.controls; }

  constructor(
    private configHelper: ConfigHelper,
    private accountService: AccountService,
    private formBuilder: FormBuilder,
    private toastService: HotToastService,
  ) { }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();
    this.createForm();
    this.accountService.getChildAccountIdentityInfo(this.userAccountId).subscribe(
      (result) => {
        this.proofOfIdentity = result.content;
        this.userForm.patchValue(this.proofOfIdentity);
        this.userForm.controls.birthDate.setValue(dayjs(this.proofOfIdentity.birthDate).format('YYYY-MM-DD'));
        this.userForm.controls.idDocExpiryDate.setValue(dayjs(this.proofOfIdentity.idDocExpiryDate).format('YYYY-MM-DD'));
        this.userForm.controls.gender.disable();
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  public get IdDocTypeEnum() {
    return IdDocTypeEnum;
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  createForm() {
    if (!this.userForm) {
      this.userForm = this.formBuilder.group({
        citizenshipCountryId: ['', Validators.required],
        firstLanguageId: ['', Validators.required],
        firstName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
        middleName: ['', Validators.pattern(this.namePattern)],
        lastName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
        gender: ['', Validators.required],
        birthDate: ['', Validators.required],
        idDocTypeId: ['', this.documentTypeIdValid(this.personalLookupInfo)],
        idDocNumber: ['', [Validators.required, Validators.pattern(this.alphaNumericaPattern)]],
        idDocExpiryDate: ['', Validators.required],
        childUserAccountId: [this.userAccountId]
      });
    }
  }

  checkBirthDateValidity() {
    if (this.f.birthDate.valid) {
      const now = dayjs();
      const birthday = dayjs(this.f.birthDate.value);
      if (birthday > now) {
        this.showBirthDateError = true;
      } else {
        this.showBirthDateError = false;
      }
    }
  }

  checkExpiryDateValidity() {
    if (this.f.idDocExpiryDate.valid) {
      const now = dayjs().startOf('day');
      const day = dayjs(this.f.idDocExpiryDate.value);
      if (day < now) {
        this.showIdDocExpiryError = true;
      } else {
        this.showIdDocExpiryError = false;
      }
      if (this.userForm.controls.idDocTypeId.value === IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument) {
        this.showIdDocExpiryError = false;
      }
    }
  }

  checkAge(): boolean {
    if (this.f.birthDate.valid) {
      const now = dayjs();
      const birthday = dayjs(this.f.birthDate.value);
      const age = now.diff(birthday, "year");
      if (age < this.minimumAge) {
        if (this.brandId === BrandEnum.CELPIP) {
          this.toastService.warning("the date of birth shows the age is under " + this.minimumAge + ", please contact our office at 1-800-958-5186 or info@celpip.ca for information on how to proceed");
        } else {
          this.toastService.warning("the date of birth shows the age is under " + this.minimumAge + ", please contact our office at 1-855-520-2235 or info@cael.ca for information on how to proceed");
        }
        return false;
      }
      if (birthday > now) {
        this.toastService.warning("Please enter a valid date of birth");
        return false;
      }
      return true;
    }
  }

  documentTypeIdValid = (personalLookup: PersonalLookupInfoForTestRegistrationDto) => (control: FormControl) => {
    const selection: number = control.value * 1;
    if (personalLookup) {
      for (const idDocType of personalLookup.idDocTypes) {
        if (idDocType.id === selection) {
          return null;
        }
      }
    }
    return { requireMatch: true };
  };

  onSubmit() {
    if (this.showBirthDateError || this.showIdDocExpiryError) {
      this.toastService.warning("Please enter in valid dates");
      return;
    }
    if (this.showBirthDateError) {
      this.toastService.warning("Please enter a valid date of birth.");
      return;
    }
    Object.keys(this.userForm.controls).forEach((control: string) => {
      this.userForm.controls[control].markAsDirty();
    });
    if (!this.checkAge()) {
      return;
    }
    if (this.userForm.valid) {
      if (this.forChildReadOnlyFields) {
        this.proofOfIdentitySaveRequest = {
          childUserAccountId: this.userAccountId,
          birthDate: this.proofOfIdentity.birthDate,
          citizenshipCountryId: this.f.citizenshipCountryId.value,
          firstLanguageId: this.f.firstLanguageId.value,
          firstName: this.f.firstName.value,
          gender: this.f.gender.value,
          idDocExpiryDate: this.f.idDocExpiryDate.value,
          idDocNumber: this.f.idDocNumber.value,
          idDocTypeId: this.f.idDocTypeId.value,
          lastName: this.f.lastName.value,
          middleName: this.f.middleName.value,
        };
        this.formSubmit.emit(this.proofOfIdentitySaveRequest);
        return;
      }
      this.formSubmit.emit(this.userForm.value);
    } else {
      this.toastService.warning("Please fill in the missing fields");
    }
  }
}
